import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { SpeakerSelect } from "@/components/quotes/SpeakerSelect";

export const AssignSpeaker = ({ quote, entities, onClose, onSaved }) => {
  const names = [...new Set(quote.lines.filter((l) => !l.entity_id).map((l) => l.speaker_name || ""))];
  const [from, setFrom] = useState(names[0]);
  const [choice, setChoice] = useState(names[0] ? "stub" : "");
  const [newName, setNewName] = useState(names[0] || "");
  const [category, setCategory] = useState("Character");
  const [busy, setBusy] = useState(false);
  const creating = choice === "stub" || choice === "entry";

  const save = async () => {
    setBusy(true);
    try {
      const body = creating ? { from_name: from, create_name: newName, create_category: category, create_stub: choice === "stub" } : { from_name: from, entity_id: choice.slice(5) };
      const { data } = await api.post(`/quotes/${quote.id}/assign`, body);
      toast.success(data.created ? `Assigned to ${data.created.name} (new ${choice === "stub" ? "stub" : "entry"})` : "Speaker assigned");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-speaker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Assign speaker</DialogTitle>
          <DialogDescription>Link the unattributed lines to an entry, or create one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {names.length > 1 && (
            <Select value={from || "__none"} onValueChange={(v) => { const n = v === "__none" ? "" : v; setFrom(n); setNewName(n); }}>
              <SelectTrigger data-testid="assign-from-select"><SelectValue /></SelectTrigger>
              <SelectContent>{names.map((n) => <SelectItem key={n || "_"} value={n || "__none"}>{n || "Unattributed lines"}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <SpeakerSelect entities={entities} value={choice} onChange={setChoice} testid="assign-speaker-select"
            extra={[{ value: "stub", label: "+ Create a stub entry" }, { value: "entry", label: "+ Create a full entry" }]} />
          {creating && (
            <div className="flex gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Speaker's name" className="flex-1" data-testid="assign-new-name" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-36" data-testid="assign-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !choice || (creating && !newName.trim())} data-testid="assign-speaker-save">{busy ? "Saving…" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
